import React, { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { ChevronRight, ChevronLeft, X } from "react-feather";
import Modal from "react-modal";

const PhotoGalleryComponent = (props) => {
  const { isLoading, customContainerClassName, headingClassName, servicePage } =
    props;

  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const toggleLightbox = (index) => {
    setSelectedIndex(index);
    setLightboxIsOpen(!lightboxIsOpen);
  };

  useEffect(() => {
    if (lightboxIsOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [lightboxIsOpen]);

  let galleryAll = props.gallery;
  if (props.gallery.length > 0) {
    galleryAll = props.gallery[0];
  }

  let items = galleryAll.photos.map((item) => ({
    ...item,
    id: item.id,
    caption: "Ornamental Solutions",
    src: item.fullSize.images.fallback && item.fullSize.images.fallback.src,
    w: 1200,
    h: 900,
    alt: item.title,
  }));

  const swiperRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.update();
    }
  }, [swiperRef]);

  return (
    <div className="py-12 md:py-20 overflow-hidden bg-white">
      <div className="container mx-auto px-4 md:px-6 relative isolate">
        <svg
          className="absolute inset-x-0  top-[-200px] -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)]"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84"
              width={200}
              height={200}
              x="50%"
              y={-1}
              patternUnits="userSpaceOnUse"
            >
              <path d="M.5 200V.5H200" fill="none" />
            </pattern>
          </defs>
          <svg x="50%" y={-1} className="overflow-visible fill-gray-50">
            <path
              d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
              strokeWidth={0}
            />
          </svg>
          <rect
            width="100%"
            height="100%"
            strokeWidth={0}
            fill="url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)"
          />
        </svg>

        <h3 className="font-display text-2xl md:text-3xl font-bold text-center max-w-7xl mx-auto text-balance font-display text-gray-900">
          <span
            dangerouslySetInnerHTML={{
              __html: galleryAll.title ? galleryAll.title : "No Gallery Title",
            }}
          />
        </h3>
        {galleryAll.description && galleryAll.description.description ? (
          <p className="text-gray-700 text-center max-w-4xl mx-auto py-2 text-sm md:text-base">
            {galleryAll.description.description}
          </p>
        ) : null}

        <div className="max-w-7xl px-4 md:-px-2 flex flex-wrap mx-auto justify-center mt-6">
          {items.map(({ fullSize, caption, id, alt, ...rest }, j) => (
            <button
              onClick={() => toggleLightbox(j)}
              key={id}
              className="w-1/3 sm:w-1/4 lg:w-[13%] p-1 gatsby-img-view"
            >
              <GatsbyImage
                image={getImage(fullSize)}
                className="thumbnail-img rounded-lg max:w-[154px] md:max-h-[119px] sm:max-h-[80px] xs:max-h-[60px] max-h-[52px] shadow-xl aspect-square hover:scale-105 transform transition-all duration-300 ease-in-out resolution"
                style={{ cursor: "pointer" }}
                alt={alt}
                loading="eager"
                quality={100}
                aspectRatio={1}
              />
            </button>
          ))}
        </div>

        <p className="text-center text-gray-400 italic font-normal text-sm">
          Tap images to enlarge
        </p>
        {lightboxIsOpen && (
          <Modal
            isOpen={lightboxIsOpen}
            onRequestClose={() => setLightboxIsOpen(false)}
            closeTimeoutMS={300}
            contentLabel="Get an Estimate"
            className="Modal bg-transparent bg-opacity-80 overflow-hidden"
            overlayClassName="Overlay"
          >
            <div
              className="relative w-screen h-screen flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                className="absolute top-4 right-4 text-white rounded-full p-3 transition z-10 close-btn"
                onClick={() => setLightboxIsOpen(false)}
              >
                <X />
              </button>

              {/* Left Arrow */}
              {selectedIndex > 0 && (
                <button
                  className="bg-[#fff3] absolute w-[48px] h-[48px] left-6 top-1/2 transform -translate-y-1/2 text-white rounded-full p-3 transition z-10 flex justify-center items-center"
                  onClick={() => swiperRef.current.swiper.slidePrev()}
                >
                  <ChevronLeft className="mr-[3px] mb-[3px] lg:mr-0 lg:mb-0" />
                </button>
              )}

              {/* Right Arrow */}
              {selectedIndex < galleryAll.photos.length - 1 && (
                <button
                  className="bg-[#fff3] absolute w-[48px] h-[48px] right-6 top-1/2 transform -translate-y-1/2 text-white rounded-full p-3 transition z-10 flex justify-center items-center"
                  onClick={() => swiperRef.current.swiper.slideNext()}
                >
                  <ChevronRight className="ml-[3px] mb-[3px] lg:ml-0 lg:mb-0" />
                </button>
              )}

              <p className="absolute bottom-4 left-6 text-xs text-white z-50">
                Ornamental Solutions
              </p>
              <p className="absolute bottom-4 right-6 text-xs text-white z-50">
                {selectedIndex + 1} of {galleryAll.photos.length}
              </p>

              {/* Swiper Carousel */}
              <Swiper
                ref={swiperRef}
                autoplay
                initialSlide={selectedIndex}
                onSlideChange={(swiper) => setSelectedIndex(swiper.activeIndex)}
                className="w-full lg:h-full md:h-[420px] flex items-center h-270px"
              >
                {galleryAll.photos.map((photo, index) => (
                  <SwiperSlide
                    key={photo.id}
                    className="flex justify-center items-center"
                  >
                    <GatsbyImage
                      image={getImage(photo.fullSize)}
                      alt={photo.title || "Gallery Image"}
                      className="carousal-image object-contain h-full xl:mx-[236px]"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default PhotoGalleryComponent;
